import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleContact = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('提交成功！我们会尽快与您联系。');
      form.reset();
      return;
    }
    const formData = new FormData(form);
    try {
      const response = await fetch('https://readdy.ai/api/form/d9n1igu8mbnljin59sr0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功！我们会尽快与您联系。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于51视频',
    description: '51视频是一个专业的在线视频平台，提供高清免费影视内容。了解我们的品牌故事、发展历程和联系方式。',
    url: 'https://www.51video.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '51视频',
      url: 'https://www.51video.com/',
    },
    about: {
      '@type': 'Organization',
      name: '51视频',
      description: '专业的在线视频平台，提供高清免费影视内容',
      foundingDate: '2018',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'service@51video.com',
        telephone: '400-888-5151',
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: '北京',
        addressRegion: '朝阳区',
        addressCountry: 'CN',
      },
    },
  };

  const milestones = [
    { year: '2018', title: '平台创立', desc: '51视频正式上线，致力于为用户提供高清免费影视内容。' },
    { year: '2019', title: '用户突破百万', desc: '凭借流畅的播放体验和丰富的内容，注册用户突破100万。' },
    { year: '2020', title: '内容全面升级', desc: '引入4K超清画质，全面覆盖电影、电视剧、综艺、动漫等品类。' },
    { year: '2022', title: '用户突破千万', desc: '平台用户突破1000万，成为国内领先的免费在线视频平台。' },
    { year: '2024', title: '技术革新', desc: '采用新一代视频传输技术，实现零缓冲播放体验，用户满意度达99%。' },
    { year: '2026', title: '全新启程', desc: '用户突破5000万，51视频在线高清免费观看，持续为全球华语用户提供优质服务。' },
  ];

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen bg-background-950">
        <Navbar />

        <main className="pt-20">
          {/* Page Header */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-12">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
              <div className="mt-6 text-center">
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-100 font-heading">
                  关于51视频
                </h1>
                <p className="text-foreground-500 text-sm md:text-base mt-3 max-w-2xl mx-auto leading-relaxed">
                  51视频提供51视频在线、91在线视频等高清免费影视内容，播放流畅不卡顿，
                  致力于为每一位用户带来极致的在线观影体验。
                </p>
              </div>
            </div>
          </section>

          {/* Brand Story */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14 bg-background-900/50">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading mb-4">
                    品牌故事
                  </h2>
                  <p className="text-foreground-400 text-sm leading-relaxed mb-4">
                    51视频成立于2018年，始终秉持"免费·高清·流畅"的核心理念，为广大用户提供优质的在线视频服务。
                    从最初的几十部影视资源起步，到如今拥有超过10万部高清影视内容，51视频在线高清免费观看已经成为数千万用户的观影首选。
                  </p>
                  <p className="text-foreground-400 text-sm leading-relaxed mb-4">
                    我们深知用户对视频播放流畅度的追求，持续投入技术研发，采用先进的CDN加速和智能转码技术，
                    确保51视频在线和91在线视频的播放体验始终流畅不缓冲。无论您是使用电脑、平板还是手机，
                    都能享受到高清流畅的观影体验。
                  </p>
                  <p className="text-foreground-400 text-sm leading-relaxed">
                    51视频不仅是一个视频播放平台，更是连接优质内容与观众的桥梁。
                    我们与国内外多家内容提供商建立了深度合作关系，持续丰富平台内容库，
                    涵盖电影、电视剧、综艺、动漫、纪录片、体育等多个领域，满足不同用户的观影需求。
                  </p>
                </div>
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20media%20company%20office%20with%20large%20screens%20displaying%20video%20content%2C%20creative%20workspace%20with%20warm%20lighting%2C%20professional%20yet%20cozy%20atmosphere%2C%20technology%20and%20entertainment%20fusion%2C%20clean%20modern%20design&width=800&height=500&seq=about-brand-01&orientation=landscape"
                    alt="51视频品牌故事"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading text-center mb-10">
                发展历程
              </h2>
              <div className="relative">
                <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-background-800 -translate-x-1/2"></div>
                <div className="space-y-8">
                  {milestones.map((m, i) => (
                    <div key={i} className={`flex flex-col md:flex-row items-center gap-4 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                        <div className="p-5 rounded-xl bg-background-900 border border-background-800/50">
                          <span className="text-accent-500 text-sm font-bold">{m.year}</span>
                          <h3 className="text-foreground-200 font-semibold mt-1 text-sm">{m.title}</h3>
                          <p className="text-foreground-500 text-xs mt-1 leading-relaxed">{m.desc}</p>
                        </div>
                      </div>
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500 text-background-950 font-bold text-sm flex-shrink-0 z-10">
                        {m.year.slice(2)}
                      </div>
                      <div className="flex-1 hidden md:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14 bg-background-900/50">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading text-center mb-10">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { name: '张先生', role: '影视爱好者', comment: '51视频在线真的太方便了，高清免费还不卡顿。我已经推荐给身边所有朋友了，每天下班回家就打开51视频看电影。', rating: 5 },
                  { name: '李女士', role: '全职妈妈', comment: '在家带孩子的时候喜欢看电视剧，91在线视频资源很全，播放也很流畅不缓冲。全集免费看，太良心了！', rating: 5 },
                  { name: '王同学', role: '大学生', comment: '宿舍网速一般，但51视频播放很流畅。动漫资源特别全，更新也快，追番必备神器！', rating: 5 },
                ].map((review, i) => (
                  <div key={i} className="p-6 rounded-xl bg-background-900 border border-background-800/50">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: review.rating }).map((_, j) => (
                        <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-foreground-400 text-sm leading-relaxed mb-4">{review.comment}</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                        <span className="text-primary-400 text-sm font-bold">{review.name[0]}</span>
                      </div>
                      <div>
                        <div className="text-foreground-300 text-sm font-medium">{review.name}</div>
                        <div className="text-foreground-600 text-xs">{review.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading text-center mb-4">
                联系我们
              </h2>
              <p className="text-foreground-500 text-sm text-center mb-8">
                如有任何问题或建议，请随时与我们联系。我们的客服团队将在24小时内回复您。
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                {[
                  { icon: 'ri-mail-line', label: '电子邮箱', value: 'service@51video.com' },
                  { icon: 'ri-phone-line', label: '客服电话', value: '400-888-5151' },
                  { icon: 'ri-map-pin-line', label: '公司地址', value: '北京市朝阳区科技园区' },
                ].map((info, i) => (
                  <div key={i} className="p-5 rounded-xl bg-background-900 border border-background-800/50 text-center">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400">
                      <i className={`${info.icon} text-lg`}></i>
                    </div>
                    <div className="text-foreground-500 text-xs mb-1">{info.label}</div>
                    <div className="text-foreground-200 text-sm font-medium">{info.value}</div>
                  </div>
                ))}
              </div>

              <form onSubmit={handleContact} data-readdy-form className="space-y-4 p-6 rounded-xl bg-background-900 border border-background-800/50">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-foreground-400 text-xs mb-1.5">姓名</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="请输入您的姓名"
                      className="w-full px-4 py-2.5 bg-background-800 border border-background-700 rounded-lg text-sm text-foreground-200 placeholder-foreground-600 outline-none focus:border-primary-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-foreground-400 text-xs mb-1.5">邮箱</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="请输入您的邮箱"
                      className="w-full px-4 py-2.5 bg-background-800 border border-background-700 rounded-lg text-sm text-foreground-200 placeholder-foreground-600 outline-none focus:border-primary-500 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-foreground-400 text-xs mb-1.5">主题</label>
                  <input
                    type="text"
                    name="subject"
                    required
                    placeholder="请输入留言主题"
                    className="w-full px-4 py-2.5 bg-background-800 border border-background-700 rounded-lg text-sm text-foreground-200 placeholder-foreground-600 outline-none focus:border-primary-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-foreground-400 text-xs mb-1.5">留言内容</label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请输入您的留言内容（最多500字）"
                    className="w-full px-4 py-2.5 bg-background-800 border border-background-700 rounded-lg text-sm text-foreground-200 placeholder-foreground-600 outline-none focus:border-primary-500 transition-colors resize-none"
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-antispam-field"
                />
                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-950 font-semibold rounded-lg transition-all whitespace-nowrap text-sm"
                >
                  提交留言
                </button>
                {formStatus !== 'idle' && (
                  <p className={`text-sm text-center ${formStatus === 'success' ? 'text-accent-500' : 'text-red-400'}`}>
                    {formMessage}
                  </p>
                )}
              </form>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}